import { useState, useRef, useEffect } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import {
  Search, Send, Paperclip, Video, Phone, Info,
  AlertTriangle, Clock, Circle,
} from 'lucide-react';

/* ═══ MOCK DATA ═══ */
const CHATS = [
  { id: 1, name: 'Inf. Marco Rossi', role: 'Infermiere ADI', avatar: 'MR', tab: 'equipe', lastMsg: 'Ho completato la medicazione, valori stabili.', time: '10:45', unread: 2 },
  { id: 2, name: 'Dott. Carla Bianchi', role: 'Medico UVMD', avatar: 'CB', tab: 'equipe', lastMsg: 'Confermo rivalutazione per giovedi.', time: '09:30', unread: 0 },
  { id: 3, name: 'Ft. Elena Ferrara', role: 'Fisioterapista', avatar: 'EF', tab: 'equipe', lastMsg: 'Seduta n.6 completata. Progressi evidenti.', time: 'Ieri', unread: 0 },
  { id: 4, name: 'COT Distretto 7', role: 'Centrale Operativa', avatar: 'CO', tab: 'cot', lastMsg: 'Alert: SpO2 paziente Verdi < 90%. Verificare.', time: '11:02', unread: 1 },
  { id: 5, name: 'Anna Verdi (Caregiver)', role: 'Caregiver familiare', avatar: 'AV', tab: 'pazienti', lastMsg: 'Buongiorno, mia madre ha avuto febbre stanotte.', time: '08:15', unread: 3 },
  { id: 6, name: 'Luca Esposito (Paziente)', role: 'Paziente ADI', avatar: 'LE', tab: 'pazienti', lastMsg: 'Grazie per la visita di ieri.', time: 'Ieri', unread: 0 },
  { id: 7, name: 'Dott. Paolo Neri', role: 'Specialista Cardiologo', avatar: 'PN', tab: 'equipe', lastMsg: 'Referto ecocardio disponibile nel fascicolo.', time: 'Mar', unread: 0 },
];

const MESSAGES = {
  1: [
    { id: 1, from: 'them', text: 'Buongiorno, sono appena arrivato dalla paziente Verdi.', time: '10:30' },
    { id: 2, from: 'them', text: 'Parametri vitali: PA 135/80, FC 72, SpO2 96%. Medicazione eseguita regolarmente, ferita in via di guarigione.', time: '10:32' },
    { id: 3, from: 'me', text: 'Ottimo Marco, grazie per l\'aggiornamento. Continua con il protocollo attuale.', time: '10:38' },
    { id: 4, from: 'them', text: 'Ho completato la medicazione, valori stabili. Prossimo accesso venerdi.', time: '10:45' },
    { id: 5, from: 'me', text: 'Perfetto. Se noti variazioni nella ferita, inviami una foto per valutazione.', time: '10:47' },
  ],
  4: [
    { id: 1, from: 'system', text: 'Alert Telemonitoraggio: SpO2 Paziente Verdi Anna scesa sotto 90% alle ore 10:55. Ultimo valore registrato: 88%. Richiesta verifica urgente.', time: '11:02' },
    { id: 2, from: 'me', text: 'Ricevuto. Contatto immediatamente l\'infermiere di turno per verifica domiciliare.', time: '11:05' },
  ],
  5: [
    { id: 1, from: 'them', text: 'Buongiorno dottoressa, sono Anna, la figlia della signora Verdi.', time: '08:00' },
    { id: 2, from: 'them', text: 'Mia madre ha avuto febbre stanotte, 38.2. Ha anche tossito molto. Cosa devo fare?', time: '08:15' },
    { id: 3, from: 'me', text: 'Buongiorno Anna, grazie per la segnalazione. Misuri la temperatura ogni 4 ore e somministri paracetamolo come da prescrizione.', time: '08:45' },
    { id: 4, from: 'me', text: 'Invio subito l\'infermiere per un controllo. Le faro sapere quando arriva.', time: '08:46' },
  ],
};

/* Fallback messages for chats without specific messages */
function getMessages(chatId) {
  if (MESSAGES[chatId]) return MESSAGES[chatId];
  const chat = CHATS.find(c => c.id === chatId);
  return [
    { id: 1, from: 'them', text: chat?.lastMsg || 'Ciao!', time: chat?.time || '09:00' },
  ];
}

/* ═══ MAIN COMPONENT ═══ */
export default function ComunicazioniCaseManager() {
  const [activeTab, setActiveTab] = useState('all');
  const [selectedChat, setSelectedChat] = useState(CHATS[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [inputMsg, setInputMsg] = useState('');
  const messagesEndRef = useRef(null);

  const filteredChats = CHATS.filter(c => {
    if (searchTerm && !c.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (activeTab === 'all') return true;
    return c.tab === activeTab;
  });

  const messages = getMessages(selectedChat?.id);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedChat]);

  const TABS = [
    { id: 'all', label: 'Tutte' },
    { id: 'equipe', label: 'Equipe' },
    { id: 'pazienti', label: 'Pazienti' },
    { id: 'cot', label: 'Alert COT' },
  ];

  return (
    <AppLayout role="case-manager">
      <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="comunicazioni-page">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white mb-4" data-testid="comunicazioni-title">Hub Comunicazioni e Telemedicina</h1>

        {/* Main Chat Container */}
        <div className="flex h-[700px] w-full bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden" data-testid="chat-container">

          {/* ══ LEFT: Chat List ══ */}
          <div className="w-1/3 border-r border-slate-200 dark:border-slate-800 flex flex-col shrink-0" data-testid="chat-sidebar">
            {/* Search */}
            <div className="p-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Cerca conversazione..."
                  className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm text-foreground placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 transition-colors"
                  data-testid="chat-search"
                />
              </div>
            </div>

            {/* Tabs */}
            <div className="flex px-3 pt-3 pb-1 gap-1 shrink-0" data-testid="chat-tabs">
              {TABS.map(t => (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-colors
                    ${activeTab === t.id
                      ? 'bg-cyan-50 dark:bg-cyan-950/40 text-cyan-700 dark:text-cyan-400'
                      : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                  data-testid={`tab-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Chat List */}
            <div className="flex-1 overflow-y-auto" data-testid="chat-list">
              {filteredChats.map(chat => (
                <div
                  key={chat.id}
                  onClick={() => setSelectedChat(chat)}
                  className={`flex items-center gap-3 px-4 py-3.5 border-b border-slate-100 dark:border-slate-800/60 cursor-pointer transition-colors
                    ${selectedChat?.id === chat.id
                      ? 'bg-cyan-50/60 dark:bg-cyan-950/20'
                      : 'hover:bg-slate-50 dark:hover:bg-slate-800/40'}`}
                  data-testid={`chat-item-${chat.id}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-xs font-bold
                    ${chat.tab === 'cot' ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400'
                      : chat.tab === 'pazienti' ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}
                  >
                    {chat.avatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{chat.name}</p>
                      <span className="text-[10px] text-slate-400 dark:text-slate-500 shrink-0 ml-2">{chat.time}</span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">{chat.lastMsg}</p>
                  </div>
                  {chat.unread > 0 && (
                    <span className="w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0" data-testid={`unread-${chat.id}`}>
                      {chat.unread}
                    </span>
                  )}
                </div>
              ))}
              {filteredChats.length === 0 && (
                <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-10">Nessuna conversazione trovata</p>
              )}
            </div>
          </div>

          {/* ══ RIGHT: Active Conversation ══ */}
          <div className="flex-1 flex flex-col bg-slate-50 dark:bg-[#0f172a]" data-testid="chat-area">
            {/* Chat Header */}
            <div className="h-[72px] border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-6 flex items-center justify-between shrink-0" data-testid="chat-header">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold
                  ${selectedChat?.tab === 'cot' ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400'
                    : selectedChat?.tab === 'pazienti' ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}
                >
                  {selectedChat?.avatar}
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{selectedChat?.name}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <Circle className="w-2 h-2 text-emerald-500 fill-emerald-500" />
                    <span className="text-[11px] text-slate-500 dark:text-slate-400">{selectedChat?.role}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1" data-testid="chat-actions">
                <button className="w-10 h-10 rounded-lg flex items-center justify-center text-slate-500 hover:text-cyan-600 dark:hover:text-cyan-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" title="Avvia Teleconsulto" data-testid="btn-video">
                  <Video className="w-5 h-5" />
                </button>
                <button className="w-10 h-10 rounded-lg flex items-center justify-center text-slate-500 hover:text-cyan-600 dark:hover:text-cyan-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" title="Chiama" data-testid="btn-phone">
                  <Phone className="w-5 h-5" />
                </button>
                <button className="w-10 h-10 rounded-lg flex items-center justify-center text-slate-500 hover:text-cyan-600 dark:hover:text-cyan-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" title="Dettagli" data-testid="btn-info">
                  <Info className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-5" data-testid="messages-area">
              {messages.map(msg => {
                if (msg.from === 'system') {
                  return (
                    <div key={msg.id} className="flex justify-center" data-testid="msg-system">
                      <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 text-amber-800 dark:text-amber-300 px-4 py-2.5 rounded-xl max-w-[85%] text-xs">
                        <AlertTriangle className="w-4 h-4 shrink-0" />
                        <span>{msg.text}</span>
                        <span className="text-[10px] text-amber-500 dark:text-amber-500/70 shrink-0 ml-2">{msg.time}</span>
                      </div>
                    </div>
                  );
                }
                if (msg.from === 'me') {
                  return (
                    <div key={msg.id} className="flex gap-3 max-w-[80%] ml-auto justify-end" data-testid="msg-sent">
                      <div>
                        <div className="bg-cyan-600 p-3.5 rounded-2xl rounded-tr-sm shadow-sm text-white text-sm leading-relaxed">{msg.text}</div>
                        <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1 text-right">{msg.time}</p>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={msg.id} className="flex gap-3 max-w-[80%]" data-testid="msg-received">
                    <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-[10px] font-bold text-slate-500 dark:text-slate-300 shrink-0 mt-1">
                      {selectedChat?.avatar}
                    </div>
                    <div>
                      <div className="bg-white dark:bg-slate-800 p-3.5 rounded-2xl rounded-tl-sm shadow-sm text-slate-800 dark:text-slate-200 text-sm leading-relaxed">{msg.text}</div>
                      <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">{msg.time}</p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-4 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 shrink-0" data-testid="chat-input-area">
              <div className="flex items-center gap-3">
                <button className="w-10 h-10 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors shrink-0" title="Allega file" data-testid="btn-attach">
                  <Paperclip className="w-5 h-5" />
                </button>
                <input
                  type="text"
                  value={inputMsg}
                  onChange={e => setInputMsg(e.target.value)}
                  placeholder="Scrivi un messaggio..."
                  className="flex-1 h-11 px-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm text-foreground placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 transition-colors"
                  data-testid="msg-input"
                  onKeyDown={e => { if (e.key === 'Enter' && inputMsg.trim()) setInputMsg(''); }}
                />
                <button
                  className="w-11 h-11 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white flex items-center justify-center shrink-0 transition-colors disabled:opacity-40"
                  disabled={!inputMsg.trim()}
                  data-testid="btn-send"
                >
                  <Send className="w-4.5 h-4.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
